import { useCallback, useEffect, useRef, useState } from 'react'

import { knowledgeThreadsApi } from '@/lib/api'

import {
  ThreadPlanExecutionView,
  ThreadPlanLifecycle,
  ThreadPlanProposal,
  ThreadPlanSkillHandoff,
  ThreadPlanStepHandoff,
  ThreadPlanStepProposal,
  ThreadPlanStepState,
  ThreadPlanStepStatus,
} from './threadPlan.types'

/**
 * Plan execution cursor (Wave 3 / Step 7).
 *
 * Server store keyed on thread + proposing message token. The proposal JSON
 * still lives on the assistant message; this hook tracks lifecycle and which
 * step is current. On first read, a leftover localStorage cursor is PUT so
 * an in-flight plan is not orphaned by deploy. A dismissed/done/superseded
 * cursor never resurrects as proposed (server refuses that overwrite; the
 * hook prefers a terminal record on GET).
 *
 * Sounds good activates the next *spend* step. Leading in-thread skill
 * steps (audience, drafts already in the transcript) are skipped so approval
 * does not fire extra turns. A remaining skill-only plan still starts the
 * first skill. A workflow step opens ThreadCommandSeed. A step reaches
 * `done` only on a completing turn or a terminal run. The plan reaches `done`
 * only when its last step did. Skip advances one step and continues;
 * remaining work is never bulk-finished. Spend still goes through intake +
 * ThreadRunGate — never this hook.
 */

const PLAN_EXEC_STORAGE_PREFIX = 'thread-plan-exec:'

const TERMINAL_LIFECYCLES: ReadonlySet<ThreadPlanLifecycle> = new Set([
  'done',
  'dismissed',
  'superseded',
])

export function threadPlanExecutionStorageKey(
  threadToken: string,
  messageId: string,
): string {
  return `${PLAN_EXEC_STORAGE_PREFIX}${threadToken}:${messageId}`
}

function getPlanExecutionStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isStepStatus(value: unknown): value is ThreadPlanStepStatus {
  return (
    value === 'pending' ||
    value === 'current' ||
    value === 'running' ||
    value === 'done' ||
    value === 'failed' ||
    value === 'skipped'
  )
}

function isLifecycle(value: unknown): value is ThreadPlanLifecycle {
  return (
    value === 'proposed' ||
    value === 'active' ||
    value === 'done' ||
    value === 'dismissed' ||
    value === 'superseded'
  )
}

function parseExecutionView(value: unknown): ThreadPlanExecutionView | null {
  if (!isRecord(value)) {
    return null
  }
  if (typeof value.messageId !== 'string' || value.messageId.length === 0) {
    return null
  }
  if (!isLifecycle(value.lifecycle)) {
    return null
  }
  if (!Array.isArray(value.steps) || value.steps.length < 1) {
    return null
  }

  const steps: Array<ThreadPlanStepState> = []
  for (const raw of value.steps) {
    if (!isRecord(raw)) {
      return null
    }
    if (typeof raw.slug !== 'string' || raw.slug.length === 0) {
      return null
    }
    if (raw.kind !== 'skill' && raw.kind !== 'workflow') {
      return null
    }
    if (typeof raw.displayName !== 'string' || raw.displayName.length === 0) {
      return null
    }
    if (!isStepStatus(raw.status)) {
      return null
    }
    const flags = Array.isArray(raw.flags)
      ? raw.flags.filter((flag): flag is string => typeof flag === 'string')
      : undefined
    const title =
      typeof raw.title === 'string' && raw.title.trim()
        ? raw.title.trim()
        : undefined
    const ask =
      typeof raw.ask === 'string' && raw.ask.trim()
        ? raw.ask.trim()
        : undefined
    let inputs: ThreadPlanStepState['inputs']
    if (
      raw.inputs &&
      typeof raw.inputs === 'object' &&
      !Array.isArray(raw.inputs)
    ) {
      const audienceRaw = (raw.inputs as Record<string, unknown>).audience
      const audience =
        typeof audienceRaw === 'string' ? audienceRaw.trim() : ''
      if (audience) {
        inputs = { audience }
      }
    }
    steps.push({
      slug: raw.slug,
      kind: raw.kind,
      displayName: raw.displayName,
      title,
      ask,
      inputs,
      flags: flags && flags.length > 0 ? flags : undefined,
      status: raw.status,
    })
  }

  const currentIndex =
    value.currentIndex === null
      ? null
      : typeof value.currentIndex === 'number' &&
          Number.isInteger(value.currentIndex) &&
          value.currentIndex >= 0 &&
          value.currentIndex < steps.length
        ? value.currentIndex
        : null

  return {
    messageId: value.messageId,
    lifecycle: value.lifecycle,
    steps,
    currentIndex,
  }
}

export function loadThreadPlanExecution(
  threadToken: string,
  messageId: string,
): ThreadPlanExecutionView | null {
  const storage = getPlanExecutionStorage()
  if (!storage || !threadToken || !messageId) {
    return null
  }

  const key = threadPlanExecutionStorageKey(threadToken, messageId)

  try {
    let raw = storage.getItem(key)
    // Soft-migrate Wave 2 sessionStorage cursors so a dismiss survives the
    // storage change without resurrecting as proposed.
    if (!raw) {
      try {
        const fromSession = window.sessionStorage.getItem(key)
        if (fromSession) {
          storage.setItem(key, fromSession)
          window.sessionStorage.removeItem(key)
          raw = fromSession
        }
      } catch {
        // Best-effort.
      }
    }
    if (!raw) {
      return null
    }
    const parsed = parseExecutionView(JSON.parse(raw))
    if (!parsed) {
      storage.removeItem(key)
      return null
    }
    return parsed
  } catch {
    try {
      storage.removeItem(key)
    } catch {
      // Best-effort.
    }
    return null
  }
}

export function clearThreadPlanExecutionStorage(
  threadToken: string,
  messageId: string,
) {
  const storage = getPlanExecutionStorage()
  if (!storage || !threadToken || !messageId) {
    return
  }
  try {
    storage.removeItem(threadPlanExecutionStorageKey(threadToken, messageId))
  } catch {
    // Best-effort.
  }
}

function cursorPutBody(view: ThreadPlanExecutionView) {
  return {
    lifecycle: view.lifecycle,
    steps: view.steps,
    currentIndex: view.currentIndex,
  }
}

/**
 * GET wins when the server already has a terminal cursor, or when this
 * browser has not progressed past proposed. Keep a local terminal or an
 * in-flight active cursor over a stale proposed GET.
 */
export function shouldPreferRemoteCursor(
  local: ThreadPlanExecutionView | null,
  remote: ThreadPlanExecutionView,
): boolean {
  if (TERMINAL_LIFECYCLES.has(remote.lifecycle)) {
    return true
  }
  if (local && TERMINAL_LIFECYCLES.has(local.lifecycle)) {
    return false
  }
  if (local?.lifecycle === 'active' && remote.lifecycle === 'proposed') {
    return false
  }
  return true
}

export function writeThreadPlanExecution(
  threadToken: string,
  view: ThreadPlanExecutionView,
) {
  const storage = getPlanExecutionStorage()
  if (!storage || !threadToken) {
    return
  }
  const parsed = parseExecutionView(view)
  if (!parsed) {
    return
  }
  try {
    storage.setItem(
      threadPlanExecutionStorageKey(threadToken, view.messageId),
      JSON.stringify(parsed),
    )
  } catch {
    // Best-effort; in-memory state still updates.
  }
}

function proposalToView(proposal: ThreadPlanProposal): ThreadPlanExecutionView {
  return {
    messageId: proposal.messageId,
    lifecycle: 'proposed',
    currentIndex: null,
    steps: proposal.steps.map((step) => ({
      ...step,
      status: 'pending',
    })),
  }
}

export function skillStepIntent(step: ThreadPlanStepProposal): string {
  return planStepLabel(step)
}

function isFinishedStepStatus(status: ThreadPlanStepStatus): boolean {
  return status === 'skipped' || status === 'done'
}

/**
 * Move the cursor to the next runnable step at or after `fromIndex`.
 * Skipped and completed steps are never made current, so they are not executed.
 * Skip must leave remaining work to run, not stamp it done.
 */
export function advanceToNextStep(
  view: ThreadPlanExecutionView,
  fromIndex: number,
): ThreadPlanExecutionView {
  const steps = view.steps.map((step) => ({ ...step }))

  let nextIndex = fromIndex
  while (nextIndex < steps.length && isFinishedStepStatus(steps[nextIndex].status)) {
    nextIndex += 1
  }

  if (nextIndex >= steps.length) {
    return {
      ...view,
      lifecycle: 'done',
      steps,
      currentIndex: null,
    }
  }

  steps[nextIndex] = { ...steps[nextIndex], status: 'current' }
  for (let i = nextIndex + 1; i < steps.length; i += 1) {
    if (steps[i].status === 'current' || steps[i].status === 'running') {
      steps[i] = { ...steps[i], status: 'pending' }
    }
  }

  return {
    ...view,
    lifecycle: 'active',
    steps,
    currentIndex: nextIndex,
  }
}

export function planStepLabel(step: ThreadPlanStepProposal): string {
  const ask = step.ask?.trim()
  if (ask) {
    return ask
  }
  const title = step.title?.trim()
  if (title) {
    return title
  }
  return step.displayName
}

/** One-line recap after Sounds good: what was skipped, what opens next. */
export function agreedPlanRecap(plan: ThreadPlanExecutionView): string {
  const skipped = plan.steps.filter((step) => step.status === 'skipped')
  const current =
    plan.currentIndex != null ? plan.steps[plan.currentIndex] : undefined
  const parts: Array<string> = ['Agreed.']
  if (skipped.length > 0) {
    parts.push(
      `Already in this thread: ${skipped.map((step) => planStepLabel(step)).join('; ')}.`,
    )
  }
  if (current?.kind === 'workflow') {
    parts.push(
      `Next: ${planStepLabel(current)} — pick the audience, then Continue.`,
    )
  } else if (current) {
    parts.push(`Next: ${planStepLabel(current)}.`)
  }
  return parts.join(' ')
}

export function stepHandoffFrom(
  view: ThreadPlanExecutionView,
): ThreadPlanStepHandoff | null {
  if (view.lifecycle !== 'active') {
    return null
  }
  const index = view.currentIndex
  if (index == null) {
    return null
  }
  const step = view.steps[index]
  if (!step || step.status !== 'current') {
    return null
  }
  if (step.kind === 'workflow') {
    return {
      kind: 'workflow',
      slug: step.slug,
      audience: step.inputs?.audience?.trim() ?? '',
      stepIndex: index,
    }
  }
  const skill: ThreadPlanSkillHandoff = {
    kind: 'skill',
    slug: step.slug,
    intent: skillStepIntent(step),
    stepIndex: index,
  }
  return skill
}

/**
 * Skills before the first workflow are work the thread already holds
 * (named audience, drafted variants). Re-running them on Sounds good is
 * what made the screen blink, then pop `/test` with a stale audience.
 * Skill-only plans are unchanged. Skills *after* a workflow stay pending.
 */
export function skipLeadingInThreadSkills(
  steps: Array<ThreadPlanStepState>,
): Array<ThreadPlanStepState> {
  const firstWorkflow = steps.findIndex((step) => step.kind === 'workflow')
  if (firstWorkflow <= 0) {
    return steps.map((step) => ({ ...step, status: 'pending' }))
  }
  return steps.map((step, index) => {
    if (index < firstWorkflow && step.kind === 'skill') {
      return { ...step, status: 'skipped' }
    }
    return { ...step, status: 'pending' }
  })
}

function activateFromProposed(
  view: ThreadPlanExecutionView,
): ThreadPlanExecutionView {
  const reset: ThreadPlanExecutionView = {
    ...view,
    lifecycle: 'active',
    currentIndex: 0,
    steps: skipLeadingInThreadSkills(view.steps),
  }
  return advanceToNextStep(reset, 0)
}

function withStatusAt(
  view: ThreadPlanExecutionView,
  index: number,
  status: ThreadPlanStepStatus,
): ThreadPlanExecutionView {
  const steps = view.steps.map((step, i) =>
    i === index ? { ...step, status } : step,
  )
  return { ...view, steps }
}

export interface UseThreadPlanExecutionResult {
  /** Latest non-superseded view per message id (for card mount sites). */
  plansByMessageId: Record<string, ThreadPlanExecutionView>
  /**
   * Register / rehydrate a validated proposal. A newer message silently
   * supersedes any earlier proposed/active plan on this thread.
   */
  registerProposal: (proposal: ThreadPlanProposal) => void
  confirmSoundsGood: (messageId: string) => ThreadPlanStepHandoff | null
  dismiss: (messageId: string) => void
  skipCurrent: (messageId: string) => ThreadPlanStepHandoff | null
  /**
   * Failed → current again. Returns the step handoff so the page can re-send
   * a skill turn or re-seed ThreadCommandMenu.
   */
  retryCurrent: (messageId: string) => ThreadPlanStepHandoff | null
  markCurrentRunning: (messageId: string) => void
  markCurrentDone: (messageId: string) => ThreadPlanStepHandoff | null
  markCurrentFailed: (messageId: string) => void
  getPlan: (messageId: string) => ThreadPlanExecutionView | null
  /**
   * GET every open cursor so the card matches the server after an assistant
   * turn. Completes a running skill step when `messageId` is that plan.
   */
  refreshAfterTurn: (
    messageId?: string,
  ) => Promise<ThreadPlanStepHandoff | null>
}

export function useThreadPlanExecution(
  threadToken: string,
): UseThreadPlanExecutionResult {
  const [plansByMessageId, setPlansByMessageId] = useState<
    Record<string, ThreadPlanExecutionView>
  >({})
  const plansRef = useRef(plansByMessageId)
  plansRef.current = plansByMessageId
  const hydratedTokenRef = useRef(threadToken)
  const writeSeqRef = useRef<Record<string, number>>({})
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (hydratedTokenRef.current === threadToken) {
      return
    }
    hydratedTokenRef.current = threadToken
    writeSeqRef.current = {}
    setPlansByMessageId({})
  }, [threadToken])

  const applyMemory = useCallback((view: ThreadPlanExecutionView) => {
    const existing = plansRef.current[view.messageId]
    if (
      existing &&
      existing.lifecycle === view.lifecycle &&
      existing.currentIndex === view.currentIndex &&
      JSON.stringify(existing.steps) === JSON.stringify(view.steps)
    ) {
      return
    }
    const next = {
      ...plansRef.current,
      [view.messageId]: view,
    }
    plansRef.current = next
    setPlansByMessageId(next)
  }, [])

  const persist = useCallback(
    (view: ThreadPlanExecutionView) => {
      const parsed = parseExecutionView(view)
      if (!parsed) {
        return
      }
      applyMemory(parsed)
      if (!threadToken) {
        return
      }
      const seq = (writeSeqRef.current[parsed.messageId] ?? 0) + 1
      writeSeqRef.current[parsed.messageId] = seq
      void knowledgeThreadsApi
        .putPlanCursor(threadToken, parsed.messageId, cursorPutBody(parsed))
        .then((saved) => {
          if (!isMountedRef.current) {
            return
          }
          if (writeSeqRef.current[parsed.messageId] !== seq) {
            return
          }
          if (hydratedTokenRef.current !== threadToken) {
            return
          }
          const fromServer = parseExecutionView(saved)
          if (fromServer) {
            applyMemory(fromServer)
          }
          clearThreadPlanExecutionStorage(threadToken, parsed.messageId)
        })
        .catch(() => {
          if (!isMountedRef.current) {
            return
          }
          if (writeSeqRef.current[parsed.messageId] !== seq) {
            return
          }
          writeThreadPlanExecution(threadToken, parsed)
        })
    },
    [applyMemory, threadToken],
  )

  const registerProposal = useCallback(
    (proposal: ThreadPlanProposal) => {
      if (!proposal.messageId || proposal.steps.length < 2) {
        return
      }

      const inMemory = plansRef.current[proposal.messageId] ?? null
      if (inMemory && inMemory.lifecycle !== 'superseded') {
        persist(inMemory)
        return
      }

      const localLegacy = threadToken
        ? loadThreadPlanExecution(threadToken, proposal.messageId)
        : null
      const seed =
        localLegacy && localLegacy.lifecycle !== 'superseded'
          ? localLegacy
          : proposalToView(proposal)

      // Silent supersede: any other open plan on this thread is replaced.
      for (const [messageId, view] of Object.entries(plansRef.current)) {
        if (messageId === proposal.messageId) {
          continue
        }
        if (view.lifecycle === 'proposed' || view.lifecycle === 'active') {
          persist({
            ...view,
            lifecycle: 'superseded',
            currentIndex: null,
            steps: view.steps.map((step) =>
              step.status === 'current' || step.status === 'running'
                ? { ...step, status: 'pending' }
                : step,
            ),
          })
        }
      }

      applyMemory(seed)

      if (!threadToken) {
        return
      }

      const generation = hydratedTokenRef.current
      void (async () => {
        let remote: ThreadPlanExecutionView | null = null
        try {
          const fetched = await knowledgeThreadsApi.getPlanCursor(
            threadToken,
            proposal.messageId,
          )
          remote = fetched ? parseExecutionView(fetched) : null
        } catch {
          remote = null
        }
        if (hydratedTokenRef.current !== generation || !isMountedRef.current) {
          return
        }
        const current = plansRef.current[proposal.messageId] ?? seed
        if (remote && shouldPreferRemoteCursor(current, remote)) {
          applyMemory(remote)
          clearThreadPlanExecutionStorage(threadToken, proposal.messageId)
          return
        }
        persist(current)
      })()
    },
    [applyMemory, persist, threadToken],
  )

  const getPlan = useCallback((messageId: string) => {
    return plansRef.current[messageId] ?? null
  }, [])

  const confirmSoundsGood = useCallback(
    (messageId: string): ThreadPlanStepHandoff | null => {
      const current = plansRef.current[messageId]
      if (!current || current.lifecycle !== 'proposed') {
        return null
      }
      const next = activateFromProposed(current)
      persist(next)
      return stepHandoffFrom(next)
    },
    [persist],
  )

  const dismiss = useCallback(
    (messageId: string) => {
      const current = plansRef.current[messageId]
      if (
        !current ||
        current.lifecycle === 'done' ||
        current.lifecycle === 'dismissed' ||
        current.lifecycle === 'superseded'
      ) {
        return
      }
      persist({
        ...current,
        lifecycle: 'dismissed',
        currentIndex: null,
      })
    },
    [persist],
  )

  const skipCurrent = useCallback(
    (messageId: string): ThreadPlanStepHandoff | null => {
      const current = plansRef.current[messageId]
      if (!current || current.lifecycle !== 'active') {
        return null
      }
      const index = current.currentIndex
      if (index == null) {
        return null
      }
      const step = current.steps[index]
      if (
        step.status !== 'current' &&
        step.status !== 'failed' &&
        step.status !== 'running'
      ) {
        return null
      }
      const skipped = withStatusAt(current, index, 'skipped')
      const next = advanceToNextStep(skipped, index + 1)
      persist(next)
      return stepHandoffFrom(next)
    },
    [persist],
  )

  const retryCurrent = useCallback(
    (messageId: string): ThreadPlanStepHandoff | null => {
      const current = plansRef.current[messageId]
      if (!current || current.lifecycle !== 'active') {
        return null
      }
      const index = current.currentIndex
      if (index == null) {
        return null
      }
      const step = current.steps[index]
      if (step.status !== 'failed') {
        return null
      }
      const retried = withStatusAt(current, index, 'current')
      persist(retried)
      return stepHandoffFrom(retried)
    },
    [persist],
  )

  const markCurrentRunning = useCallback(
    (messageId: string) => {
      const current = plansRef.current[messageId]
      if (!current || current.lifecycle !== 'active') {
        return
      }
      const index = current.currentIndex
      if (index == null) {
        return
      }
      const step = current.steps[index]
      if (step.status !== 'current' && step.status !== 'failed') {
        return
      }
      persist(withStatusAt(current, index, 'running'))
    },
    [persist],
  )

  const markCurrentDone = useCallback(
    (messageId: string): ThreadPlanStepHandoff | null => {
      const current = plansRef.current[messageId]
      if (!current || current.lifecycle !== 'active') {
        return null
      }
      const index = current.currentIndex
      if (index == null) {
        return null
      }
      const step = current.steps[index]
      if (step.status !== 'running') {
        return null
      }
      const done = withStatusAt(current, index, 'done')
      const next = advanceToNextStep(done, index + 1)
      persist(next)
      return stepHandoffFrom(next)
    },
    [persist],
  )

  const markCurrentFailed = useCallback(
    (messageId: string) => {
      const current = plansRef.current[messageId]
      if (!current || current.lifecycle !== 'active') {
        return
      }
      const index = current.currentIndex
      if (index == null) {
        return
      }
      persist(withStatusAt(current, index, 'failed'))
    },
    [persist],
  )

  const adoptRemoteCursor = useCallback(
    (messageId: string, remote: ThreadPlanExecutionView) => {
      const parsed = parseExecutionView(remote)
      if (!parsed) {
        return null
      }
      applyMemory(parsed)
      if (threadToken) {
        clearThreadPlanExecutionStorage(threadToken, messageId)
      }
      return parsed
    },
    [applyMemory, threadToken],
  )

  const refreshPlanFromServer = useCallback(
    async (messageId: string): Promise<ThreadPlanExecutionView | null> => {
      if (!threadToken || !messageId) {
        return plansRef.current[messageId] ?? null
      }
      const seq = (writeSeqRef.current[messageId] ?? 0) + 1
      writeSeqRef.current[messageId] = seq
      try {
        const fetched = await knowledgeThreadsApi.getPlanCursor(
          threadToken,
          messageId,
        )
        if (!isMountedRef.current) {
          return plansRef.current[messageId] ?? null
        }
        if (writeSeqRef.current[messageId] !== seq) {
          return plansRef.current[messageId] ?? null
        }
        if (hydratedTokenRef.current !== threadToken) {
          return plansRef.current[messageId] ?? null
        }
        const parsed = fetched ? parseExecutionView(fetched) : null
        if (!parsed) {
          return plansRef.current[messageId] ?? null
        }
        return adoptRemoteCursor(messageId, parsed)
      } catch {
        return plansRef.current[messageId] ?? null
      }
    },
    [adoptRemoteCursor, threadToken],
  )

  const refreshAfterTurn = useCallback(
    async (messageId?: string): Promise<ThreadPlanStepHandoff | null> => {
      const ids = new Set<string>(
        Object.entries(plansRef.current)
          .filter(([, view]) => {
            return view.lifecycle === 'proposed' || view.lifecycle === 'active'
          })
          .map(([id]) => id),
      )
      if (messageId) {
        ids.add(messageId)
      }
      await Promise.all(
        Array.from(ids).map((id) => refreshPlanFromServer(id)),
      )
      if (!messageId) {
        return null
      }
      const plan = plansRef.current[messageId]
      if (!plan || plan.lifecycle !== 'active') {
        return null
      }
      const index = plan.currentIndex
      const step = index != null ? plan.steps[index] : null
      if (step?.status === 'running') {
        return markCurrentDone(messageId)
      }
      return stepHandoffFrom(plan)
    },
    [markCurrentDone, refreshPlanFromServer],
  )

  return {
    plansByMessageId,
    registerProposal,
    confirmSoundsGood,
    dismiss,
    skipCurrent,
    retryCurrent,
    markCurrentRunning,
    markCurrentDone,
    markCurrentFailed,
    getPlan,
    refreshAfterTurn,
  }
}
